import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMenu, createOrder, getOrders, lookupCustomer, getStore } from '@/services/api';
import apiClient from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Search, X, Plus, Minus, LogOut, Menu, Store, Eye, Clock, 
  Pause, Bell, Phone, HelpCircle, User, Users, Utensils, 
  Printer, Trash2, ChevronDown, ChevronUp, RefreshCw,
  MapPin, CheckCircle, XCircle, Bike, UserPlus, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import wsService from '@/services/websocket';
import { formatCurrency } from '@/utils/currency';

const POSAppNew = () => {
  const [user, setUser] = useState(null);
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [holdOrders, setHoldOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  
  // UI State
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [orderType, setOrderType] = useState('DINE_IN');
  
  // Customer & Order State
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', address: '', table: '' });
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isComplimentary, setIsComplimentary] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [isLoyalty, setIsLoyalty] = useState(false);
  
  // COMPREHENSIVE DISCOUNT SYSTEM - Apply Only (No Configuration at Billing)
  // selectedDiscount: The dashboard-configured discount selected by operator
  const [selectedDiscount, setSelectedDiscount] = useState(null); 
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [showDiscountSelector, setShowDiscountSelector] = useState(false);
  
  // Legacy states - kept for backward compatibility
  const [categoryDiscounts, setCategoryDiscounts] = useState({});
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  
  // Combo Offers State
  const [comboOffers, setComboOffers] = useState([]);
  const [appliedCombo, setAppliedCombo] = useState(null);
  const [showComboSelector, setShowComboSelector] = useState(false);
  
  // Modals
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showLiveViewModal, setShowLiveViewModal] = useState(false);
  const [showRecentModal, setShowRecentModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showAssignRiderModal, setShowAssignRiderModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [selectedOrderForRider, setSelectedOrderForRider] = useState(null);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null);
  const [editCustomerInfo, setEditCustomerInfo] = useState({ name: '', phone: '', address: '' });
  const [selectedRider, setSelectedRider] = useState('');
  
  // Store config
  const [storeConfig, setStoreConfig] = useState({ 
    tax_settings: { 
      enabled: true, 
      percent: 5,      // Total tax 5%
      cgst: 2.5,       // CGST 2.5%
      sgst: 2.5,       // SGST 2.5%
      show_breakdown: true
    },
    service_charge: {
      enabled: true,
      percent: 5       // Service charge 5%
    },
    show_images_in_pos: true
  });
  
  const [isCartExpanded, setIsCartExpanded] = useState(true);
  
  const navigate = useNavigate();

  // Get unique categories from menu
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(menu.map(item => item.category).filter(Boolean))];
    return cats;
  }, [menu]);

  // Filter menu items
  const filteredMenu = useMemo(() => {
    let items = menu;
    
    if (selectedCategory !== 'All') {
      items = items.filter(item => item.category === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query)
      );
    }
    
    if (shortCode) {
      items = items.filter(item => 
        item.sku?.toLowerCase().startsWith(shortCode.toLowerCase())
      );
    }
    
    return items;
  }, [menu, selectedCategory, searchQuery, shortCode]);

  useEffect(() => {
    document.title = 'SSPICE POS';
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.id || !userData.store_id) {
      navigate('/login');
      return;
    }
    
    const initializePOS = async () => {
      setUser(userData);
      loadMenu(userData.store_id);
      loadOrders(userData.store_id);
      loadStoreConfig(userData.store_id);
      loadHoldOrders();
    };
    initializePOS();

    wsService.connect(`pos:store:${userData.store_id}`);
    wsService.on('order.created', () => loadOrders(userData.store_id));
    wsService.on('order.updated', () => loadOrders(userData.store_id));
    wsService.on('kitchen.ready', () => loadOrders(userData.store_id));
    
    // Listen for day-end event
    wsService.on('day.end', (data) => {
      console.log('🌙 POS: Day ended - refreshing orders');
      toast.info('Day has ended - Orders cleared', { duration: 5000 });
      loadOrders(userData.store_id);
    });

    const interval = setInterval(() => loadOrders(userData.store_id), 10000);
    return () => {
      wsService.disconnect();
      clearInterval(interval);
    };
  }, [navigate]);

  const loadMenu = async (storeId) => {
    try {
      // Load all items including inactive ones (include_all=true)
      const response = await apiClient.get(`/menu/${storeId}?active_only=false&include_all=true`);
      setMenu(response.data);
    } catch (error) {
      console.error('Error loading menu:', error);
      // Fallback to old method
      try {
        const response = await getMenu(storeId);
        setMenu(response.data);
      } catch (e) {
        console.error('Fallback menu load failed:', e);
      }
    }
  };

  const loadOrders = async (storeId) => {
    try {
      const response = await getOrders(storeId, null, 100);
      const orders = response.data;
      
      // Active orders
      const active = orders.filter(o => 
        ['NEW', 'PREPARING', 'READY_DINEIN', 'READY_TAKEAWAY', 'READY_DELIVERY', 'RIDER_ASSIGNED', 'OUT_FOR_DELIVERY'].includes(o.status)
      );
      setActiveOrders(active);
      
      // Completed orders
      const completed = orders.filter(o => 
        ['SERVED', 'PICKED_UP', 'DELIVERED', 'CANCELLED'].includes(o.status)
      );
      setCompletedOrders(completed);
      
      // Load riders - try active first, then fallback to all riders
      try {
        const ridersResponse = await apiClient.get(`/riders/active?store_id=${storeId}`);
        let ridersList = ridersResponse.data?.riders || [];
        
        // If no active riders, try to get all riders from auth endpoint
        if (ridersList.length === 0) {
          try {
            const allRidersResponse = await apiClient.get(`/auth/riders?store_id=${storeId}`);
            ridersList = (allRidersResponse.data || []).map(r => ({
              rider_id: r.id,
              rider_name: r.name,
              phone: r.phone,
              status: r.active ? 'available' : 'offline'
            }));
          } catch (e) {
            console.log('Could not fetch all riders');
          }
        }
        setRiders(ridersList);
      } catch (e) {
        setRiders([]);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadStoreConfig = async (storeId) => {
    try {
      const response = await getStore(storeId);
      if (response.data?.config) {
        // Merge API config with defaults (to preserve tax/service charge settings)
        setStoreConfig(prev => ({
          ...prev,
          ...response.data.config,
          tax_settings: {
            ...prev.tax_settings,
            ...response.data.config?.tax_settings
          },
          service_charge: {
            ...prev.service_charge,
            ...response.data.config?.service_charge
          }
        }));
      }
      // Load available discounts from localStorage (saved from POSDashboard)
      loadAvailableDiscounts();
      // Load combo offers from API
      loadComboOffers(storeId);
    } catch (error) {
      console.error('Error loading store config:', error);
    }
  };

  const loadAvailableDiscounts = () => {
    // Get discounts saved from the POSDashboard discount configuration
    const savedDiscounts = localStorage.getItem('pos_discounts');
    if (savedDiscounts) {
      const config = JSON.parse(savedDiscounts);
      // Only show enabled discounts
      setAvailableDiscounts((config.discounts || []).filter(d => d.enabled));
    } else {
      // Default discounts
      setAvailableDiscounts([
        { id: 1, name: 'Staff Discount', type: 'percent', value: 20, enabled: true },
        { id: 2, name: 'Loyalty Discount', type: 'percent', value: 5, enabled: true },
        { id: 3, name: 'Happy Hour', type: 'percent', value: 10, enabled: true },
        { id: 4, name: '₹50 Off', type: 'fixed', value: 50, enabled: true }
      ]);
    }
  };

  // Load combo offers from API
  const loadComboOffers = async (storeId) => {
    try {
      const response = await apiClient.get(`/offers?store_id=${storeId}`);
      // Filter only enabled COMBO type offers
      const combos = (response.data || []).filter(o => o.type === 'COMBO' && o.enabled);
      setComboOffers(combos);
    } catch (error) {
      console.error('Error loading combo offers:', error);
    }
  };

  // Check if a combo can be applied to the current cart
  const checkComboApplicable = (combo) => {
    if (!combo.combo_items || combo.combo_items.length === 0) return false;
    
    // Check if all combo items are in the cart with required quantities
    for (const comboItem of combo.combo_items) {
      const cartItem = cart.find(c => c.id === comboItem.item_id);
      if (!cartItem || cartItem.qty < comboItem.quantity) {
        return false;
      }
    }
    return true;
  };

  // Get applicable combos for current cart
  const getApplicableCombos = () => {
    return comboOffers.filter(combo => checkComboApplicable(combo));
  };

  // Calculate combo savings
  const calculateComboSavings = (combo) => {
    const individualTotal = combo.combo_items.reduce((sum, ci) => {
      const menuItem = menu.find(m => m.id === ci.item_id);
      return sum + (menuItem?.price || ci.price || 0) * ci.quantity;
    }, 0);
    return individualTotal - (combo.combo_price || 0);
  };

  // Apply combo to cart
  const applyCombo = (combo) => {
    setAppliedCombo(combo);
    setShowComboSelector(false);
    toast.success(`🎉 ${combo.name} applied! You save ₹${calculateComboSavings(combo).toFixed(2)}`);
  };

  // Remove applied combo
  const removeCombo = () => {
    setAppliedCombo(null);
    toast.info('Combo removed');
  };

  const applyDiscount = (discount) => {
    setAppliedDiscount(discount);
    setShowDiscountSelector(false);
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
  };

  const loadHoldOrders = () => {
    const saved = localStorage.getItem('holdOrders');
    if (saved) {
      setHoldOrders(JSON.parse(saved));
    }
  };

  const saveHoldOrders = (orders) => {
    localStorage.setItem('holdOrders', JSON.stringify(orders));
    setHoldOrders(orders);
  };

  const handleCustomerPhoneChange = async (phone) => {
    setCustomerInfo({ ...customerInfo, phone });
    
    if (phone.length === 10) {
      setCustomerLookupLoading(true);
      try {
        const response = await lookupCustomer(user.store_id, phone);
        if (response.data.found) {
          const customer = response.data.customer;
          setCustomerInfo({
            ...customerInfo,
            name: customer.name || '',
            phone: customer.phone,
            address: customer.address || ''
          });
          toast.success('Customer found!');
        }
      } catch (error) {
        console.error('Customer lookup error:', error);
      } finally {
        setCustomerLookupLoading(false);
      }
    }
  };

  const addToCart = (item) => {
    const existing = cart.find(c => c.id === item.id);
    if (existing) {
      setCart(cart.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c));
    } else {
      // Include is_combo and combo_items data for combo items
      setCart([...cart, { 
        ...item, 
        qty: 1, 
        checked: true,
        is_combo: item.is_combo || false,
        combo_items: item.combo_items || []
      }]);
    }
    
    // Show different message for combo items
    if (item.is_combo && item.combo_items && item.combo_items.length > 0) {
      toast.success(`🎁 ${item.name} combo added (${item.combo_items.length} items)`);
    } else {
      toast.success(`${item.name} added`);
    }
  };

  const updateCartQty = (itemId, delta) => {
    setCart(cart.map(item => {
      if (item.id === itemId) {
        const newQty = item.qty + delta;
        return newQty > 0 ? { ...item, qty: newQty } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const toggleItemCheck = (itemId) => {
    setCart(cart.map(item => 
      item.id === itemId ? { ...item, checked: !item.checked } : item
    ));
  };

  const toggleItemComp = (itemId) => {
    setCart(cart.map(item => 
      item.id === itemId ? { ...item, isComp: !item.isComp } : item
    ));
  };

  const getCartTotal = () => {
    const checkedItems = cart.filter(item => item.checked);
    // Calculate subtotal excluding complimentary items
    const chargeableItems = checkedItems.filter(item => !item.isComp);
    const compItems = checkedItems.filter(item => item.isComp);
    
    let subtotal = chargeableItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
    const compTotal = compItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
    
    // Calculate combo savings if a combo is applied
    let comboSavings = 0;
    if (appliedCombo && appliedCombo.combo_items && appliedCombo.combo_price) {
      const comboItemsOriginalPrice = appliedCombo.combo_items.reduce((sum, ci) => {
        const menuItem = menu.find(m => m.id === ci.item_id);
        return sum + (menuItem?.price || 0) * ci.quantity;
      }, 0);
      comboSavings = comboItemsOriginalPrice - appliedCombo.combo_price;
    }
    
    // Apply combo savings to subtotal
    subtotal = Math.max(0, subtotal - comboSavings);
    
    // ========== COMPREHENSIVE DISCOUNT ENGINE ==========
    // Priority: Item > Category > Bill
    // Uses selectedDiscount from dashboard configuration
    let discountAmount = 0;
    let discountBreakdown = []; // Track breakdown for display
    
    if (!isComplimentary && selectedDiscount) {
      // Check if current order type is allowed for this discount
      const currentOrderType = orderType.toUpperCase().replace('-', '_').replace(' ', '_');
      const allowedModes = selectedDiscount.allowedModes || [];
      
      // Map order types to mode IDs
      const modeMapping = {
        'DINE_IN': 'DINE_IN',
        'DINEIN': 'DINE_IN',
        'TAKEAWAY': 'TAKEAWAY',
        'PICKUP': 'TAKEAWAY',
        'DELIVERY': 'DELIVERY',
        'SWIGGY': 'SWIGGY',
        'ZOMATO': 'ZOMATO'
      };
      const mappedMode = modeMapping[currentOrderType] || currentOrderType;
      
      if (allowedModes.includes(mappedMode)) {
        // Apply discounts with priority: Item > Category > Bill
        chargeableItems.forEach(item => {
          const itemId = item.id;
          const itemCategory = item.category || 'Uncategorized';
          const itemTotal = item.price * item.qty;
          let itemDiscountPercent = 0;
          let discountSource = '';
          
          // Priority 1: Item-level discount
          if (selectedDiscount.itemDiscounts?.[itemId]?.percent > 0) {
            itemDiscountPercent = selectedDiscount.itemDiscounts[itemId].percent;
            discountSource = 'item';
          }
          // Priority 2: Category-level discount
          else if (selectedDiscount.categoryDiscounts?.[itemCategory] > 0) {
            itemDiscountPercent = selectedDiscount.categoryDiscounts[itemCategory];
            discountSource = 'category';
          }
          // Priority 3: Bill-level fallback discount
          else if (selectedDiscount.billDiscount > 0) {
            itemDiscountPercent = selectedDiscount.billDiscount;
            discountSource = 'bill';
          }
          
          if (itemDiscountPercent > 0) {
            const itemDiscount = (itemTotal * itemDiscountPercent) / 100;
            discountAmount += itemDiscount;
            
            // Track for breakdown display (group by category/source)
            const existingBreakdown = discountBreakdown.find(b => 
              b.source === discountSource && b.category === itemCategory
            );
            if (existingBreakdown) {
              existingBreakdown.subtotal += itemTotal;
              existingBreakdown.discount += itemDiscount;
            } else {
              discountBreakdown.push({
                category: discountSource === 'item' ? item.name : itemCategory,
                percent: itemDiscountPercent,
                subtotal: itemTotal,
                discount: itemDiscount,
                source: discountSource
              });
            }
          }
        });
      }
    }
    
    const afterDiscount = subtotal - discountAmount;
    
    // Service charge - ONLY if taxes are enabled
    const taxSettings = storeConfig.tax_settings || {};
    const taxEnabled = taxSettings.enabled === true;
    
    const serviceChargeSettings = storeConfig.service_charge || {};
    let serviceCharge = 0;
    if (taxEnabled && serviceChargeSettings.enabled && serviceChargeSettings.percent) {
      serviceCharge = (afterDiscount * serviceChargeSettings.percent) / 100;
    }
    
    const afterServiceCharge = afterDiscount + serviceCharge;
    
    // Tax calculation
    let cgst = 0, sgst = 0, tax = 0;
    
    if (taxEnabled) {
      if (taxSettings.cgst && taxSettings.sgst) {
        cgst = (afterServiceCharge * taxSettings.cgst) / 100;
        sgst = (afterServiceCharge * taxSettings.sgst) / 100;
        tax = cgst + sgst;
      } else if (taxSettings.percent) {
        tax = (afterServiceCharge * taxSettings.percent) / 100;
        cgst = tax / 2;
        sgst = tax / 2;
      }
    }
    
    const total = isComplimentary ? 0 : afterServiceCharge + tax;
    
    return { subtotal, compTotal, discountAmount, discountBreakdown, comboSavings, afterDiscount, serviceCharge, cgst, sgst, tax, total, taxEnabled };
  };

  const handleSaveOrder = async (printType = null) => {
    try {
      if (cart.length === 0) {
        toast.error('Cart is empty');
        return;
      }

      if (orderType === 'DELIVERY' && !customerInfo.address) {
        toast.error('Delivery address is required');
        return;
      }

      const checkedItems = cart.filter(item => item.checked);
      if (checkedItems.length === 0) {
        toast.error('No items selected');
        return;
      }

      const { total, discountAmount } = getCartTotal();

      const orderData = {
        store_id: user.store_id,
        order_type: orderType,
        source: 'pos',
        items: checkedItems.map(item => ({
          menu_item_id: item.id,
          menu_item_name: item.name,
          qty: item.qty,
          price: item.price,
          notes: ''
        })),
        customer: customerInfo.phone ? {
          name: customerInfo.name,
          phone: customerInfo.phone,
          address: customerInfo.address
        } : null,
        discount: isComplimentary ? total : discountAmount,
        discount_info: appliedDiscount ? {
          name: appliedDiscount.name,
          type: appliedDiscount.type,
          value: appliedDiscount.value
        } : null,
        payment_method: paymentMethod,
        payment_status: isPaid ? 'PAID' : 'PENDING',
        special_instructions: specialInstructions,
        is_complimentary: isComplimentary
      };

      const order = await createOrder(orderData);
      toast.success(`Order #${order.data.order_number} created!`);
      
      // Handle printing
      if (printType === 'print' || printType === 'kot_print') {
        await handlePrintBill(order.data);
      }
      if (printType === 'kot' || printType === 'kot_print') {
        await handlePrintKOT(order.data);
      }
      if (printType === 'ebill') {
        await handleSendEBill(order.data);
      }

      // Reset state
      resetOrderState();
      loadOrders(user.store_id);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order');
    }
  };

  const resetOrderState = () => {
    setCart([]);
    setCustomerInfo({ name: '', phone: '', address: '', table: '' });
    setPaymentMethod('CASH');
    setSpecialInstructions('');
    setIsComplimentary(false);
    setIsPaid(false);
    setIsLoyalty(false);
    setSelectedDiscount(null); // Clear selected discount
    setAppliedDiscount(null);
    setCategoryDiscounts({});
  };

  const handleHoldOrder = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    const holdOrder = {
      id: Date.now(),
      cart: [...cart],
      customerInfo: { ...customerInfo },
      orderType,
      paymentMethod,
      specialInstructions,
      createdAt: new Date().toISOString()
    };
    
    const newHoldOrders = [...holdOrders, holdOrder];
    saveHoldOrders(newHoldOrders);
    resetOrderState();
    toast.success('Order held');
  };

  const handleResumeHoldOrder = (holdOrder) => {
    setCart(holdOrder.cart);
    setCustomerInfo(holdOrder.customerInfo);
    setOrderType(holdOrder.orderType);
    setPaymentMethod(holdOrder.paymentMethod);
    setSpecialInstructions(holdOrder.specialInstructions);
    
    const newHoldOrders = holdOrders.filter(o => o.id !== holdOrder.id);
    saveHoldOrders(newHoldOrders);
    setShowHoldModal(false);
    toast.success('Order resumed');
  };

  const handleDeleteHoldOrder = (holdOrderId) => {
    const newHoldOrders = holdOrders.filter(o => o.id !== holdOrderId);
    saveHoldOrders(newHoldOrders);
    toast.success('Hold order deleted');
  };

  const handlePrintKOT = async (order) => {
    try {
      const response = await apiClient.post(`/prints/${order.id}/kot`);
      const { print_id } = response.data;
      const printUrl = `${process.env.REACT_APP_BACKEND_URL}/api/prints/${print_id}/print-page`;
      window.open(printUrl, '_blank');
    } catch (error) {
      console.error('Print KOT error:', error);
      toast.error('Failed to print KOT');
    }
  };

  const handlePrintBill = async (order) => {
    try {
      const response = await apiClient.post(`/prints/${order.id}/bill`);
      const { print_id } = response.data;
      const printUrl = `${process.env.REACT_APP_BACKEND_URL}/api/prints/${print_id}/print-page`;
      window.open(printUrl, '_blank');
    } catch (error) {
      console.error('Print Bill error:', error);
      toast.error('Failed to print bill');
    }
  };

  // Send E-Bill via WhatsApp/SMS
  const handleSendEBill = async (order) => {
    try {
      const response = await apiClient.post(`/prints/${order.id}/ebill`);
      const { whatsapp_link, bill_url, customer_phone, whatsapp_message } = response.data;
      
      if (whatsapp_link && customer_phone) {
        // Open WhatsApp with pre-filled message
        window.open(whatsapp_link, '_blank');
        toast.success(`E-Bill sent to ${customer_phone} via WhatsApp!`);
      } else if (bill_url) {
        // No customer phone - just copy the link
        navigator.clipboard.writeText(bill_url);
        toast.success('Bill link copied to clipboard! Share with customer.');
        // Also open the bill in new tab
        window.open(bill_url, '_blank');
      } else {
        toast.error('Unable to generate E-Bill');
      }
    } catch (error) {
      console.error('E-Bill error:', error);
      toast.error('Failed to send E-Bill');
    }
  };

  // Open edit customer modal for an order
  const handleOpenEditCustomer = (order) => {
    setSelectedOrderForEdit(order);
    setEditCustomerInfo({
      name: order.customer?.name || '',
      phone: order.customer?.phone || '',
      address: order.customer?.address || ''
    });
    setShowEditCustomerModal(true);
  };

  // Update customer info on existing order
  const handleUpdateOrderCustomer = async () => {
    try {
      if (!selectedOrderForEdit) return;
      
      await apiClient.put(`/orders/${selectedOrderForEdit.id}`, {
        customer: {
          name: editCustomerInfo.name,
          phone: editCustomerInfo.phone,
          address: editCustomerInfo.address
        }
      });
      
      toast.success('Customer details updated!');
      setShowEditCustomerModal(false);
      setSelectedOrderForEdit(null);
      setEditCustomerInfo({ name: '', phone: '', address: '' });
      loadOrders(user.store_id);
    } catch (error) {
      console.error('Update customer error:', error);
      toast.error('Failed to update customer details');
    }
  };

  // Order management functions
  const handleMarkServed = async (orderId) => {
    try {
      await apiClient.put(`/orders/${orderId}/status`, { status: 'SERVED' });
      toast.success('Order marked as served');
      loadOrders(user.store_id);
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleMarkPickedUp = async (orderId) => {
    try {
      await apiClient.put(`/orders/${orderId}/status`, { status: 'PICKED_UP' });
      toast.success('Order marked as picked up');
      loadOrders(user.store_id);
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleAssignRider = async () => {
    try {
      if (!selectedRider || !selectedOrderForRider) {
        toast.error('Please select a rider');
        return;
      }
      const rider = riders.find(r => r.rider_id === selectedRider);
      await apiClient.put(`/orders/${selectedOrderForRider.id}/assign-rider`, {
        rider_id: rider.rider_id,
        rider_name: rider.rider_name
      });
      toast.success(`Rider ${rider.rider_name} assigned`);
      setShowAssignRiderModal(false);
      setSelectedRider('');
      setSelectedOrderForRider(null);
      loadOrders(user.store_id);
    } catch (error) {
      toast.error('Failed to assign rider');
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await apiClient.put(`/orders/${orderId}/cancel`, { cancel_reason: 'Cancelled from POS' });
      toast.success('Order cancelled');
      loadOrders(user.store_id);
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'NEW': 'bg-blue-500',
      'PREPARING': 'bg-yellow-500',
      'READY_DINEIN': 'bg-green-500',
      'READY_TAKEAWAY': 'bg-green-500',
      'READY_DELIVERY': 'bg-purple-500',
      'RIDER_ASSIGNED': 'bg-indigo-500',
      'OUT_FOR_DELIVERY': 'bg-cyan-500',
      'SERVED': 'bg-gray-500',
      'PICKED_UP': 'bg-gray-500',
      'DELIVERED': 'bg-gray-500',
      'CANCELLED': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getTimeSince = (createdAt) => {
    const diff = Math.floor((new Date() - new Date(createdAt)) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m`;
    const hours = Math.floor(diff / 60);
    return `${hours}h ${diff % 60}m`;
  };

  const { subtotal, compTotal, discountAmount, discountBreakdown, serviceCharge, cgst, sgst, tax, total, taxEnabled } = getCartTotal();

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {/* Top Header Bar */}
      <div className="bg-[#C41E3A] text-white shrink-0">
        <div className="flex items-center h-11 px-2 gap-2">
          {/* Logo Section */}
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="text-white hover:bg-red-700 h-8 w-8">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-1">
              <div className="bg-orange-500 rounded-lg p-1">
                <Utensils className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold text-sm whitespace-nowrap">SSPICE</span>
              <span className="text-[9px] opacity-80 hidden lg:inline whitespace-nowrap">(Designed by Prashant B)</span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-7 text-xs px-2" onClick={() => resetOrderState()}>
              New Order
            </Button>
            <Button size="sm" variant="outline" className="text-white border-white/40 hover:bg-red-700 h-7 text-xs px-2" onClick={() => navigate('/pos-dashboard')}>
              Dashboard
            </Button>
          </div>

          {/* Icons - Moved Left with Bigger Icons */}
          <div className="flex items-center gap-0.5 shrink-0">
            {[
              { icon: Eye, label: 'On/Off', onClick: () => navigate('/pos-dashboard') },
              { icon: Eye, label: 'Live', onClick: () => setShowLiveViewModal(true) },
              { icon: Clock, label: 'Orders', onClick: () => setShowOrdersModal(true), badge: activeOrders.length },
              { icon: Clock, label: 'Recent', onClick: () => setShowRecentModal(true) },
              { icon: Pause, label: 'Hold', onClick: () => setShowHoldModal(true), badge: holdOrders.length },
            ].map((item, idx) => (
              <Button 
                key={idx}
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-red-700 flex flex-col items-center justify-center h-10 w-12 p-0 relative"
                onClick={item.onClick}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-[9px] leading-tight">{item.label}</span>
                {item.badge > 0 && (
                  <Badge className="absolute -top-0.5 -right-0.5 h-4 w-4 p-0 flex items-center justify-center bg-orange-500 text-[8px]">
                    {item.badge}
                  </Badge>
                )}
              </Button>
            ))}
          </div>

          {/* Spacer - Pushes remaining content right */}
          <div className="flex-1" />

          {/* Logout Button - Right side */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-red-700 flex flex-col items-center justify-center h-10 px-3"
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              navigate('/login');
            }}
          >
            <LogOut className="h-5 w-5" />
            <span className="text-[9px] leading-tight">Logout</span>
          </Button>
        </div>
      </div>

      {/* Search Bar Only */}
      <div className="bg-white border-b flex items-center h-9 px-2 gap-2 shrink-0">
        {/* Search Inputs */}
        <div className="flex items-center gap-1">
          <div className="relative">
            <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
            <Input 
              placeholder="Search" 
              className="pl-6 h-7 w-24 text-xs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Input 
            placeholder="Code" 
            className="h-7 w-16 text-xs"
            value={shortCode}
            onChange={(e) => setShortCode(e.target.value)}
          />
        </div>
        
        <div className="flex-1" />
      </div>

      {/* Main Content Area - PetPooja-style Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT - Categories Sidebar (~15-20% width) */}
        <div className="hidden sm:block w-[120px] lg:w-[140px] bg-[#2D2D2D] shrink-0">
          <ScrollArea className="h-full">
            <div className="py-0.5">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`w-full text-left px-3 py-2 text-[11px] font-medium transition-colors border-l-3 ${
                    selectedCategory === category
                      ? 'bg-green-600 text-white border-l-4 border-green-400'
                      : 'text-gray-300 hover:bg-gray-700 border-l-4 border-transparent'
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* CENTER - Menu Items Grid (~50% width) */}
        <div className="flex-1 overflow-hidden bg-gray-100">
          {/* Mobile Category Tabs */}
          <div className="sm:hidden flex overflow-x-auto bg-[#2D2D2D] px-1 py-1 gap-1">
            {categories.map((category) => (
              <button
                key={category}
                className={`shrink-0 px-3 py-1.5 text-[10px] font-medium rounded transition-colors ${
                  selectedCategory === category
                    ? 'bg-green-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
          
          <ScrollArea className="h-full">
            <div className="p-2">
              {/* 3-column grid like PetPooja */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {filteredMenu.map((item) => (
                  <Card
                    key={item.id}
                    className={`cursor-pointer hover:shadow-lg transition-all border-2 ${
                      !item.active ? 'grayscale opacity-50 border-gray-300' : 
                      item.is_combo ? 'border-purple-300 hover:border-purple-500' : 
                      'border-gray-200 hover:border-green-500'
                    }`}
                    onClick={() => item.active && addToCart(item)}
                  >
                    <CardContent className="p-2 text-center">
                      {/* ON/OFF Badge + Combo Badge */}
                      <div className="flex justify-between mb-1">
                        {item.is_combo ? (
                          <Badge className="h-4 text-[8px] px-1.5 bg-purple-500">
                            🎁 COMBO
                          </Badge>
                        ) : (
                          <span></span>
                        )}
                        <Badge 
                          variant={item.active ? "default" : "secondary"} 
                          className={`h-4 text-[8px] px-1.5 ${item.active ? 'bg-green-500' : 'bg-gray-400'}`}
                        >
                          {item.active ? 'ON' : 'OFF'}
                        </Badge>
                      </div>
                      
                      {/* Item Icon */}
                      <div className={`h-10 flex items-center justify-center mb-1 ${!item.active ? 'grayscale' : ''}`}>
                        {storeConfig.show_images_in_pos && item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="h-10 w-10 object-cover rounded" onError={(e) => e.target.style.display = 'none'} />
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.is_combo ? 'bg-purple-100' : item.active ? 'bg-orange-100' : 'bg-gray-100'}`}>
                            <Utensils className={`h-5 w-5 ${item.is_combo ? 'text-purple-500' : item.active ? 'text-orange-500' : 'text-gray-400'}`} />
                          </div>
                        )}
                      </div>
                      
                      {/* Item Name */}
                      <p className={`text-[10px] font-medium line-clamp-2 min-h-[2rem] leading-tight ${!item.active ? 'text-gray-400' : 'text-gray-800'}`}>
                        {item.name}
                      </p>
                      
                      {/* Combo Items Count */}
                      {item.is_combo && item.combo_items && item.combo_items.length > 0 && (
                        <p className="text-[8px] text-purple-500 mt-0.5">
                          {item.combo_items.length} items included
                        </p>
                      )}
                      
                      {/* Price */}
                      <p className={`text-xs font-bold mt-1 ${item.is_combo ? 'text-purple-600' : item.active ? 'text-green-600' : 'text-gray-400'}`}>
                        {formatCurrency(item.price)}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </ScrollArea>
        </div>

        {/* RIGHT - Cart/Billing Panel (~35% width) - PetPooja Style */}
        <div className="w-[280px] sm:w-[300px] md:w-[340px] lg:w-[380px] bg-white border-l flex flex-col shrink-0">
          {/* Order Type Tabs - TOP OF CART */}
          <div className="flex border-b">
            {[
              { type: 'DINE_IN', label: 'Dine In', color: 'gray' },
              { type: 'DELIVERY', label: 'Delivery', color: 'red' },
              { type: 'TAKEAWAY', label: 'Pick Up', color: 'green' },
            ].map((tab) => (
              <button
                key={tab.type}
                className={`flex-1 py-2 font-semibold text-xs transition-colors ${
                  orderType === tab.type 
                    ? tab.color === 'green' ? 'bg-green-600 text-white' 
                      : tab.color === 'red' ? 'bg-red-600 text-white'
                      : 'bg-gray-700 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => setOrderType(tab.type)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Customer & Icons Row */}
          <div className="flex items-center justify-between px-2 py-1.5 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 rounded-full bg-gray-200"
                onClick={() => setShowCustomerModal(true)}
              >
                <User className="h-4 w-4" />
              </Button>
              {customerInfo.name && (
                <span className="text-[10px] font-medium">{customerInfo.name}</span>
              )}
              {customerInfo.table && (
                <Badge variant="outline" className="text-[9px] h-5">T:{customerInfo.table}</Badge>
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <Printer className="h-4 w-4" />
            </Button>
          </div>

          {/* Cart Header - Like PetPooja */}
          <div className="flex items-center px-2 py-1.5 bg-gray-100 border-b text-[9px] font-bold text-gray-600 uppercase">
            <span className="flex-1">Items</span>
            <span className="w-16 text-center">Check</span>
            <span className="w-14 text-center">Qty.</span>
            <span className="w-14 text-right">Price</span>
          </div>

          {/* Cart Items Area */}
          <ScrollArea className="flex-1 min-h-[100px]">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-8 text-gray-400">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <Utensils className="h-8 w-8 text-gray-300" />
                </div>
                <p className="text-sm font-medium text-gray-500">No Item Selected</p>
                <p className="text-[10px] text-gray-400 text-center px-4">Please Select Item from Left Menu Item</p>
              </div>
            ) : (
              <div className="divide-y">
                {cart.map((item) => (
                  <div 
                    key={item.id} 
                    className={`px-2 py-1.5 ${item.isComp ? 'bg-pink-50' : 'hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center">
                      {/* Delete & Item Name */}
                      <div className="flex-1 flex items-center gap-1 min-w-0">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-5 w-5 text-red-500 hover:text-red-700 p-0 shrink-0"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                        <div className="min-w-0">
                          <p className={`text-[10px] font-medium truncate ${item.isComp ? 'text-pink-600 line-through' : ''}`} title={item.name}>
                            {item.is_combo && <span className="text-purple-600">🎁 </span>}
                            {item.name}
                          </p>
                        </div>
                      </div>
                      
                      {/* Check & Comp */}
                      <div className="w-16 flex items-center justify-center gap-1">
                        <Checkbox 
                          checked={item.checked} 
                          onCheckedChange={() => toggleItemCheck(item.id)}
                          className="h-4 w-4"
                        />
                        <Button 
                          variant={item.isComp ? "default" : "ghost"} 
                          size="icon" 
                          className={`h-5 w-5 p-0 ${item.isComp ? 'bg-pink-500 text-white' : 'text-pink-400 hover:bg-pink-50'}`}
                          onClick={() => toggleItemComp(item.id)}
                        >
                          <span className="text-[9px]">🎁</span>
                        </Button>
                      </div>
                      
                      {/* Quantity */}
                      <div className="w-14 flex items-center justify-center">
                        <Button variant="outline" size="icon" className="h-5 w-5 p-0" onClick={() => updateCartQty(item.id, -1)}>
                          <Minus className="h-2.5 w-2.5" />
                        </Button>
                        <span className="w-5 text-center font-bold text-[10px]">{item.qty}</span>
                        <Button variant="outline" size="icon" className="h-5 w-5 p-0" onClick={() => updateCartQty(item.id, 1)}>
                          <Plus className="h-2.5 w-2.5" />
                        </Button>
                      </div>
                      
                      {/* Price */}
                      <span className={`w-14 text-right text-[10px] font-bold ${item.isComp ? 'text-pink-500 line-through' : 'text-gray-800'}`}>
                        {formatCurrency(item.price * item.qty)}
                      </span>
                    </div>
                    
                    {/* Show combo included items */}
                    {item.is_combo && item.combo_items && item.combo_items.length > 0 && (
                      <div className="ml-6 mt-1 mb-1 pl-2 border-l-2 border-purple-200">
                        {item.combo_items.map((ci, idx) => (
                          <p key={idx} className="text-[9px] text-purple-600">
                            • {ci.quantity || 1}x {ci.item_name}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Cart Footer - PetPooja Style */}
          <div className="border-t bg-white shrink-0">
            {/* Action Buttons Row */}
            <div className="flex items-center gap-1 px-2 py-1.5 border-b">
              <Button size="sm" variant="outline" className="flex-1 h-7 text-[9px] font-medium" onClick={() => toast.info('BOGO coming soon')}>
                Bogo Offer
              </Button>
              <Button size="sm" variant="outline" className="flex-1 h-7 text-[9px] font-medium" onClick={() => setShowSplitModal(true)}>
                Split
              </Button>
              <Button 
                size="sm" 
                variant={selectedDiscount ? "default" : "outline"} 
                className={`flex-1 h-7 text-[9px] font-medium ${selectedDiscount ? 'bg-green-600' : ''}`} 
                onClick={() => setShowDiscountSelector(true)}
              >
                {selectedDiscount 
                  ? `✓ ${selectedDiscount.name.substring(0, 10)}` 
                  : 'Discount'}
              </Button>
              <div className="flex items-center gap-1 px-2">
                <Checkbox id="complimentary" checked={isComplimentary} onCheckedChange={setIsComplimentary} className="h-4 w-4" />
                <Label htmlFor="complimentary" className="text-[9px]">Comp</Label>
              </div>
            </div>

            {/* Special Notes / Kitchen Instructions */}
            <div className="px-2 py-1.5 border-b">
              <Input
                placeholder="Kitchen notes / Special instructions..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                className="h-7 text-[10px]"
              />
            </div>

            {/* Price Summary */}
            <div className="px-3 py-1.5 space-y-0.5 text-[10px] bg-gray-50 border-b">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              {compTotal > 0 && (
                <div className="flex justify-between text-pink-600">
                  <span>Comp Items:</span>
                  <span>-{formatCurrency(compTotal)}</span>
                </div>
              )}
              {/* Category-wise Discounts Display */}
              {discountBreakdown && discountBreakdown.length > 0 && !isComplimentary && (
                <div className="space-y-1">
                  {discountBreakdown.map((db, idx) => (
                    <div key={idx} className="flex justify-between text-purple-600 text-[10px]">
                      <span>{db.category} ({db.percent}%)</span>
                      <span>-{formatCurrency(db.discount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-purple-700 font-medium border-t border-purple-200 pt-1">
                    <span className="flex items-center gap-1">
                      Total Discount
                      <button className="text-red-500" onClick={() => setCategoryDiscounts({})}><X className="h-2.5 w-2.5" /></button>
                    </span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                </div>
              )}
              {/* Show selected discount name when discount is applied */}
              {selectedDiscount && discountAmount > 0 && !isComplimentary && (!discountBreakdown || discountBreakdown.length === 0) && (
                <div className="flex justify-between text-green-600">
                  <span className="flex items-center gap-1">
                    ✓ {selectedDiscount.name}
                    <button className="text-red-500" onClick={() => setSelectedDiscount(null)}><X className="h-2.5 w-2.5" /></button>
                  </span>
                  <span>-{formatCurrency(discountAmount)}</span>
                </div>
              )}
              {/* Service Charge */}
              {serviceCharge > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Service Charge ({storeConfig.service_charge?.percent || 0}%):</span>
                  <span>{formatCurrency(serviceCharge)}</span>
                </div>
              )}
              {/* Tax Breakdown - CGST + SGST */}
              {tax > 0 && (
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>CGST ({storeConfig.tax_settings?.cgst || 2.5}%):</span>
                    <span>{formatCurrency(cgst)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>SGST ({storeConfig.tax_settings?.sgst || 2.5}%):</span>
                    <span>{formatCurrency(sgst)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Total - Large Display */}
            <div className="flex justify-between items-center px-3 py-2 bg-gray-800 text-white">
              <span className="text-sm font-bold">Total</span>
              {isComplimentary ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs line-through opacity-60">{formatCurrency(subtotal + tax)}</span>
                  <span className="text-xl font-bold">0 🎁</span>
                </div>
              ) : (
                <span className="text-xl font-bold">{formatCurrency(total)}</span>
              )}
            </div>

            {/* Payment Methods */}
            <div className="flex items-center gap-1 px-2 py-1.5 border-b">
              {['Cash', 'Card', 'Due', 'UPI'].map((method) => (
                <Button
                  key={method}
                  size="sm"
                  variant={paymentMethod === method.toUpperCase() ? "default" : "outline"}
                  className={`flex-1 h-7 text-[9px] font-medium ${paymentMethod === method.toUpperCase() ? 'bg-green-600' : ''}`}
                  onClick={() => setPaymentMethod(method.toUpperCase())}
                >
                  {paymentMethod === method.toUpperCase() && '✓ '}{method}
                </Button>
              ))}
              <Button size="sm" variant="outline" className="h-7 text-[9px] px-2">More</Button>
            </div>

            {/* Checkboxes */}
            <div className="flex items-center gap-4 px-3 py-1 border-b">
              <div className="flex items-center gap-1">
                <Checkbox id="isPaid" checked={isPaid} onCheckedChange={setIsPaid} className="h-4 w-4" />
                <Label htmlFor="isPaid" className="text-[10px]">It&apos;s Paid</Label>
              </div>
              <div className="flex items-center gap-1">
                <Checkbox id="isLoyalty" checked={isLoyalty} onCheckedChange={setIsLoyalty} className="h-4 w-4" />
                <Label htmlFor="isLoyalty" className="text-[10px]">Loyalty</Label>
              </div>
            </div>

            {/* Save Buttons - Like PetPooja */}
            <div className="grid grid-cols-6 gap-0.5 p-1.5">
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-[8px] h-8 px-0" onClick={() => handleSaveOrder()}>Save</Button>
              <Button size="sm" className="bg-green-700 hover:bg-green-800 text-[8px] h-8 px-0" onClick={() => handleSaveOrder('print')}>Save & Print</Button>
              <Button size="sm" className="bg-green-800 hover:bg-green-900 text-[8px] h-8 px-0" onClick={() => handleSaveOrder('ebill')}>EBill</Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-[8px] h-8 px-0" onClick={() => handleSaveOrder('kot')}>KOT</Button>
              <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-[8px] h-8 px-0" onClick={() => handleSaveOrder('kot_print')}>KOT+P</Button>
              <Button size="sm" className="bg-gray-600 hover:bg-gray-700 text-[8px] h-8 px-0" onClick={handleHoldOrder}>Hold</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div>
              <Label className="text-sm">Phone Number</Label>
              <Input
                type="tel"
                placeholder="10-digit phone"
                value={customerInfo.phone}
                onChange={(e) => handleCustomerPhoneChange(e.target.value)}
                maxLength={10}
              />
              {customerLookupLoading && <p className="text-xs text-gray-500 mt-1">Looking up...</p>}
            </div>
            <div>
              <Label className="text-sm">Name</Label>
              <Input
                placeholder="Customer name"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
              />
            </div>
            {orderType === 'DINE_IN' && (
              <div>
                <Label className="text-sm">Table Number</Label>
                <Input
                  placeholder="Table #"
                  value={customerInfo.table}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, table: e.target.value })}
                />
              </div>
            )}
            {orderType === 'DELIVERY' && (
              <div>
                <Label className="text-sm">Delivery Address *</Label>
                <Textarea
                  placeholder="Full address"
                  value={customerInfo.address}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                  rows={2}
                />
              </div>
            )}
            <div>
              <Label className="text-sm">Special Instructions</Label>
              <Textarea
                placeholder="Kitchen notes..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerModal(false)}>Cancel</Button>
            <Button className="bg-green-500" onClick={() => setShowCustomerModal(false)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold Orders Modal */}
      <Dialog open={showHoldModal} onOpenChange={setShowHoldModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Hold Orders ({holdOrders.length})</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-80">
            {holdOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No orders on hold</div>
            ) : (
              <div className="space-y-2">
                {holdOrders.map((order) => (
                  <Card key={order.id} className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-sm">{order.orderType}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.createdAt).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(order.cart.reduce((s, i) => s + i.price * i.qty, 0))}</p>
                        <p className="text-xs text-gray-500">{order.cart.length} items</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-green-500 h-8"
                        onClick={() => handleResumeHoldOrder(order)}
                      >
                        Resume
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        className="h-8"
                        onClick={() => handleDeleteHoldOrder(order.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Live View Modal - NEW IMPLEMENTATION */}
      <Dialog open={showLiveViewModal} onOpenChange={setShowLiveViewModal}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Live Order View</span>
              <Button size="sm" variant="outline" onClick={() => loadOrders(user?.store_id)}>
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh]">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100">
                  <TableHead className="w-20">Order #</TableHead>
                  <TableHead className="w-24">Type</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-24">Time</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="w-24 text-right">Total</TableHead>
                  <TableHead className="w-40">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No active orders
                    </TableCell>
                  </TableRow>
                ) : (
                  activeOrders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-gray-50">
                      <TableCell className="font-bold">#{order.order_number}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {order.order_type === 'DINE_IN' && '🍽️'}
                          {order.order_type === 'TAKEAWAY' && '📦'}
                          {order.order_type === 'DELIVERY' && '🚴'}
                          {' '}{order.order_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(order.status)} text-white text-xs`}>
                          {order.status.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {getTimeSince(order.created_at)}
                      </TableCell>
                      <TableCell>
                        {order.customer ? (
                          <div className="text-xs">
                            <p className="font-medium">{order.customer.name}</p>
                            <p className="text-gray-500">{order.customer.phone}</p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Walk-in</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs max-w-[150px]">
                          {order.items?.slice(0, 2).map((item, idx) => (
                            <span key={idx}>{item.qty}x {item.menu_item_name}{idx < 1 && order.items.length > 1 ? ', ' : ''}</span>
                          ))}
                          {order.items?.length > 2 && <span className="text-gray-500"> +{order.items.length - 2} more</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold text-orange-600">
                        {formatCurrency(order.total)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {order.order_type === 'DINE_IN' && order.status === 'READY_DINEIN' && (
                            <Button size="sm" className="h-6 text-[10px] bg-green-500" onClick={() => handleMarkServed(order.id)}>
                              <CheckCircle className="h-3 w-3 mr-0.5" /> Served
                            </Button>
                          )}
                          {order.order_type === 'TAKEAWAY' && order.status === 'READY_TAKEAWAY' && (
                            <Button size="sm" className="h-6 text-[10px] bg-green-500" onClick={() => handleMarkPickedUp(order.id)}>
                              <CheckCircle className="h-3 w-3 mr-0.5" /> Picked
                            </Button>
                          )}
                          {order.order_type === 'DELIVERY' && order.status === 'READY_DELIVERY' && (
                            <Button 
                              size="sm" 
                              className="h-6 text-[10px] bg-purple-500"
                              onClick={() => {
                                setSelectedOrderForRider(order);
                                setShowAssignRiderModal(true);
                              }}
                            >
                              <UserPlus className="h-3 w-3 mr-0.5" /> Rider
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px]"
                            onClick={() => handleOpenEditCustomer(order)}
                            title="Edit Customer"
                          >
                            <User className="h-3 w-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px] bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100"
                            onClick={() => handlePrintBill(order)}
                            title="Print Bill"
                          >
                            Bill
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px] bg-orange-50 border-orange-300 text-orange-600 hover:bg-orange-100"
                            onClick={() => handlePrintKOT(order)}
                            title="Print KOT"
                          >
                            KOT
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px] bg-green-50 border-green-300 text-green-600 hover:bg-green-100"
                            onClick={() => handleSendEBill(order)}
                            title="Send E-Bill via WhatsApp"
                          >
                            E-Bill
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            className="h-6 text-[10px]"
                            onClick={() => handleCancelOrder(order.id)}
                          >
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Active Orders Modal */}
      <Dialog open={showOrdersModal} onOpenChange={setShowOrdersModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Active Orders ({activeOrders.length})</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {activeOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No active orders</div>
            ) : (
              <div className="space-y-2">
                {activeOrders.map((order) => (
                  <Card key={order.id} className="p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold">#{order.order_number}</p>
                        <Badge className={`${getStatusColor(order.status)} text-white mt-1`}>{order.status}</Badge>
                        <p className="text-xs text-gray-500 mt-1">{order.order_type}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{formatCurrency(order.total)}</p>
                        <p className="text-xs text-gray-500">{order.items?.length || 0} items</p>
                      </div>
                    </div>
                    {order.customer && (
                      <div className="mt-2 text-sm text-gray-600">
                        <p>{order.customer.name} - {order.customer.phone}</p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Recent Orders Modal */}
      <Dialog open={showRecentModal} onOpenChange={setShowRecentModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Recent Completed Orders ({completedOrders.length})</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {completedOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No completed orders</div>
            ) : (
              <div className="space-y-2">
                {completedOrders.slice(0, 20).map((order) => (
                  <Card key={order.id} className={`p-3 ${order.status === 'CANCELLED' ? 'border-red-300' : ''}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className={`font-bold ${order.status === 'CANCELLED' ? 'text-red-600' : ''}`}>
                          #{order.order_number}
                        </p>
                        <Badge className={`${getStatusColor(order.status)} text-white mt-1`}>{order.status}</Badge>
                        <p className="text-xs text-gray-500 mt-1">{order.order_type}</p>
                        {order.customer && (
                          <p className="text-xs text-gray-600 mt-1">
                            {order.customer.name} - {order.customer.phone}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-lg ${order.status === 'CANCELLED' ? 'text-red-600' : ''}`}>
                          {formatCurrency(order.total)}
                        </p>
                        <p className="text-xs text-gray-500">{getTimeSince(order.created_at)} ago</p>
                        <div className="flex gap-1 mt-2 justify-end">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px]"
                            onClick={() => handleOpenEditCustomer(order)}
                          >
                            <User className="h-3 w-3 mr-1" /> Edit Customer
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px]"
                            onClick={() => handlePrintBill(order)}
                          >
                            <Printer className="h-3 w-3 mr-1" /> Print
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Assign Rider Modal */}
      <Dialog open={showAssignRiderModal} onOpenChange={setShowAssignRiderModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Assign Rider to Order #{selectedOrderForRider?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label>Select Rider</Label>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => loadOrders(user?.store_id)}
              >
                Refresh
              </Button>
            </div>
            <Select value={selectedRider} onValueChange={setSelectedRider}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a rider" />
              </SelectTrigger>
              <SelectContent>
                {riders && riders.length > 0 ? (
                  riders.map((rider) => (
                    <SelectItem key={rider.rider_id} value={rider.rider_id}>
                      {rider.rider_name} ({rider.phone})
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none" disabled>No riders online</SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">{riders?.length || 0} rider(s) online</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignRiderModal(false)}>Cancel</Button>
            <Button className="bg-purple-600" onClick={handleAssignRider} disabled={!selectedRider}>
              Assign Rider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Bill Modal */}
      <Dialog open={showSplitModal} onOpenChange={setShowSplitModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Split Bill</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center text-gray-500">
            Split bill feature coming soon
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSplitModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advance Order Modal */}
      <Dialog open={showAdvanceModal} onOpenChange={setShowAdvanceModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Advance Order</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center text-gray-500">
            Advance order scheduling coming soon
          </div>
          <DialogFooter>
            <Button onClick={() => setShowAdvanceModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Modal */}
      <Dialog open={showEditCustomerModal} onOpenChange={setShowEditCustomerModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Edit Customer - Order #{selectedOrderForEdit?.order_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Customer Name</Label>
              <Input
                placeholder="Enter customer name"
                value={editCustomerInfo.name}
                onChange={(e) => setEditCustomerInfo({ ...editCustomerInfo, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Phone Number</Label>
              <Input
                type="tel"
                placeholder="10-digit mobile number"
                value={editCustomerInfo.phone}
                onChange={(e) => setEditCustomerInfo({ ...editCustomerInfo, phone: e.target.value })}
                maxLength={10}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Delivery Address</Label>
              <Textarea
                placeholder="Enter full delivery address"
                value={editCustomerInfo.address}
                onChange={(e) => setEditCustomerInfo({ ...editCustomerInfo, address: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>
            {selectedOrderForEdit && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>Order Type:</strong> {selectedOrderForEdit.order_type}</p>
                <p><strong>Total:</strong> {formatCurrency(selectedOrderForEdit.total)}</p>
                <p><strong>Status:</strong> {selectedOrderForEdit.status}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditCustomerModal(false);
              setSelectedOrderForEdit(null);
            }}>
              Cancel
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleUpdateOrderCustomer}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIMPLIFIED Discount Selector Modal - APPLY ONLY, NO CONFIGURATION */}
      <Dialog open={showDiscountSelector} onOpenChange={setShowDiscountSelector}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Apply Discount
            </DialogTitle>
            <p className="text-xs text-gray-500">
              Select a pre-configured discount. Order mode: <Badge variant="outline" className="ml-1">{orderType}</Badge>
            </p>
          </DialogHeader>
          <ScrollArea className="max-h-[55vh]">
            <div className="space-y-3 py-4">
              {/* Filter discounts by current order mode */}
              {(() => {
                const currentOrderType = orderType.toUpperCase().replace('-', '_').replace(' ', '_');
                const modeMapping = {
                  'DINE_IN': 'DINE_IN', 'DINEIN': 'DINE_IN',
                  'TAKEAWAY': 'TAKEAWAY', 'PICKUP': 'TAKEAWAY',
                  'DELIVERY': 'DELIVERY', 'SWIGGY': 'SWIGGY', 'ZOMATO': 'ZOMATO'
                };
                const mappedMode = modeMapping[currentOrderType] || currentOrderType;
                
                // Get discounts from localStorage
                const savedDiscounts = JSON.parse(localStorage.getItem('pos_discounts_v2') || '{"discounts":[]}');
                const allDiscounts = savedDiscounts.discounts || [];
                
                // Filter by mode and enabled status
                const applicableDiscounts = allDiscounts.filter(d => 
                  d.enabled && d.allowedModes?.includes(mappedMode)
                );
                const blockedDiscounts = allDiscounts.filter(d => 
                  d.enabled && !d.allowedModes?.includes(mappedMode)
                );
                
                return (
                  <>
                    {applicableDiscounts.length === 0 && blockedDiscounts.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Percent className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        <p>No discounts configured</p>
                        <p className="text-xs mt-2">Go to Dashboard → Discount to create discounts</p>
                      </div>
                    )}
                    
                    {applicableDiscounts.length > 0 && (
                      <>
                        <p className="text-xs font-medium text-green-600">✓ Available for {orderType}:</p>
                        <div className="space-y-2">
                          {applicableDiscounts.map((discount) => {
                            const isSelected = selectedDiscount?.id === discount.id;
                            return (
                              <div
                                key={discount.id}
                                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                  isSelected 
                                    ? 'bg-green-50 border-green-500 ring-2 ring-green-300' 
                                    : 'bg-white hover:bg-gray-50 border-gray-200'
                                }`}
                                onClick={() => {
                                  setSelectedDiscount(isSelected ? null : discount);
                                  setShowDiscountSelector(false);
                                  if (!isSelected) {
                                    toast.success(`Applied: ${discount.name}`);
                                  } else {
                                    toast.info('Discount removed');
                                  }
                                }}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-bold">{discount.name}</span>
                                  {isSelected && <Badge className="bg-green-500">Applied</Badge>}
                                </div>
                                
                                {/* Show discount summary */}
                                <div className="text-xs space-y-1 text-gray-600">
                                  {discount.categoryDiscounts && Object.keys(discount.categoryDiscounts).length > 0 && (
                                    <p>📁 Categories: {Object.entries(discount.categoryDiscounts).map(([c,p]) => `${c} ${p}%`).join(', ')}</p>
                                  )}
                                  {discount.itemDiscounts && Object.keys(discount.itemDiscounts).length > 0 && (
                                    <p>🏷️ Items: {Object.values(discount.itemDiscounts).map(i => `${i.name} ${i.percent}%`).slice(0,3).join(', ')}{Object.keys(discount.itemDiscounts).length > 3 ? '...' : ''}</p>
                                  )}
                                  {discount.billDiscount > 0 && (
                                    <p>💯 Bill: {discount.billDiscount}% (fallback)</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                    
                    {blockedDiscounts.length > 0 && (
                      <>
                        <p className="text-xs font-medium text-red-500 mt-4">✗ Not available for {orderType}:</p>
                        <div className="space-y-2 opacity-50">
                          {blockedDiscounts.map((discount) => (
                            <div
                              key={discount.id}
                              className="p-3 rounded-lg border border-red-200 bg-red-50 cursor-not-allowed"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium line-through">{discount.name}</span>
                                <Badge variant="outline" className="text-red-500 border-red-300">Blocked</Badge>
                              </div>
                              <p className="text-[10px] text-red-400 mt-1">
                                Only for: {discount.allowedModes?.join(', ')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
              
              {/* Current Selection Info */}
              {selectedDiscount && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200 mt-4">
                  <p className="text-xs font-medium text-green-700 mb-1">✓ Active Discount:</p>
                  <p className="text-sm font-bold">{selectedDiscount.name}</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full mt-2 text-red-500 border-red-200"
                    onClick={() => { setSelectedDiscount(null); setShowDiscountSelector(false); toast.info('Discount removed'); }}
                  >
                    <X className="h-3 w-3 mr-1" /> Remove Discount
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountSelector(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSAppNew;
